import { DPR } from '@constants/common.const';
import Phaser from 'phaser';
import { GameConfigService } from '@services/core/GameConfigService';

export interface ButtonConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  text: string;
  type?: 'primary' | 'secondary';
  isOutlined?: boolean;
  isDisabled?: boolean;
  onClick?: () => void | Promise<void>;
}

export class Button extends Phaser.GameObjects.Container {
  private bgRT!: Phaser.GameObjects.RenderTexture;
  private label: Phaser.GameObjects.Text;
  private hitAreaShape: Phaser.Geom.Rectangle;

  // Store config to support dynamic state changes (enable/disable)
  private config: ButtonConfig;

  constructor(config: ButtonConfig) {
    super(config.scene, config.x, config.y);
    this.config = {
      type: 'primary',
      isOutlined: false,
      isDisabled: false,
      ...config,
    };

    this.scene.add.existing(this);

    // RT for background — baked once, no earcut per frame
    const width = 240 * DPR;
    const height = 40 * DPR;
    this.bgRT = this.scene.add.renderTexture(
      -width / 2,
      -height / 2,
      width,
      height,
    );
    this.bgRT.setOrigin(0, 0);
    this.add(this.bgRT);

    // Create Text
    this.label = this.scene.add
      .text(0, 0, this.config.text.toUpperCase(), {
        fontFamily: 'korolev, sans-serif',
        fontSize: `${16 * DPR}px`,
        fontStyle: '700',
        align: 'center',
        resolution: DPR,
      })
      .setOrigin(0.5); // Center text in container
    this.add(this.label);

    // Draw the visuals based on type
    this.draw(width, height);

    // Setup Interaction
    // A Container needs a defined Hit Area to be interactive
    // 240x40 is your spec width/height
    this.setSize(width, height);
    this.hitAreaShape = new Phaser.Geom.Rectangle(0, 0, width, height);
    this.setInteractive(
      {
        gameObject: this.hitAreaShape,
        cursor: 'pointer',
      },
      Phaser.Geom.Rectangle.Contains,
    );

    // Event Listeners
    if (!this.config.isDisabled) {
      this.enableInteractivity();
    } else {
      this.setAlpha(0.5);
    }
  }

  private draw(width: number, height: number) {
    const cfg = new GameConfigService();
    const baseColor = cfg.getColorHex('--color-base-1');
    const accentColor = cfg.getColor('--color-accent-1');
    const accentColorHex = cfg.getColorHex('--color-accent-1')!;
    const primaryBtnBg = cfg.getColor(
      '--z-cta-buttons-primary-background-color',
    );
    const primaryBtnColor = cfg.getColorHex(
      '--z-cta-buttons-primary-text-color',
    );

    // Default styling values
    let fillColor = primaryBtnBg ?? accentColor!;
    let strokeColor = primaryBtnBg ?? accentColor!;
    let textColor = primaryBtnColor ?? baseColor!;

    const isPrimary = this.config.type === 'primary';

    if (isPrimary) {
      if (this.config.isOutlined && accentColorHex) {
        textColor = accentColorHex; // Outlined Primary Text matches border
      }
    } else {
      const baseBGColor = cfg.getColor('--color-base-1')!;
      const neutralColor = cfg.getColorHex('--color-neutral-100')!;

      // Secondary
      fillColor = baseBGColor;
      strokeColor = baseBGColor;
      textColor = neutralColor; // Solid Secondary Text

      if (this.config.isOutlined) {
        textColor = baseColor!; // Outlined Secondary Text matches border (assuming dark background)
      }
    }

    const radius = 8 * DPR;
    const gfx = this.scene.add.graphics();
    gfx.lineStyle(2, strokeColor, 1);

    if (this.config.isOutlined) {
      // Outlined
      gfx.strokeRoundedRect(0, 0, width, height, radius);
    } else {
      // Solid
      gfx.fillStyle(fillColor, 1);
      gfx.fillRoundedRect(0, 0, width, height, radius);
      gfx.strokeRoundedRect(0, 0, width, height, radius);
    }

    this.bgRT.draw(gfx, 0, 0);
    gfx.destroy();

    // Update Text Color
    this.label.setColor(textColor);
  }

  private enableInteractivity() {
    this.on(Phaser.Input.Events.POINTER_OVER, () => {
      this.scene.tweens.add({
        targets: this,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 100,
        ease: 'Linear',
      });
    });

    this.on(Phaser.Input.Events.POINTER_OUT, () => {
      this.scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        duration: 100,
        ease: 'Linear',
      });
    });

    this.on(Phaser.Input.Events.POINTER_UP, async () => {
      if (this.config.onClick) {
        // run onClick function and capture the result
        const result = this.config.onClick();

        // check if the result is a Promise
        if (result && result instanceof Promise) {
          this.setDisabled(true);

          try {
            await result;
          } catch (e) {
            console.error(e);
          } finally {
            // enable button if the scene is still active
            // this prevents errors if the scene was destroyed during the await
            if (this.scene) {
              this.setDisabled(false);
            }
          }
        } else {
          // if onClick function was synchronous, do nothing.
          // The action already happened.
          // We don't disable/enable because it's too fast to see.
        }
      }
    });
  }

  public setDisabled(disabled: boolean) {
    this.config.isDisabled = disabled;

    if (disabled) {
      this.setAlpha(0.5);
      this.disableInteractive();
      this.setScale(1); // Reset scale if stuck in hover state
    } else {
      this.setAlpha(1);
      this.setInteractive(
        {
          gameObject: this.hitAreaShape,
          cursor: 'pointer',
        },
        Phaser.Geom.Rectangle.Contains,
      );
      this.enableInteractivity();
    }
  }
}
